import type { ConfigResponse } from "@site-alerter/shared";
import { absoluteTime, relativeTime } from "../time.js";

/**
 * `effectivelyEnabled` is always the server's answer — the client must never
 * recompute it (SPEC §7).
 */
export function PauseBanner({ config }: { config: ConfigResponse }): JSX.Element | null {
  if (config.effectivelyEnabled) return null;

  /*
   * An automatic halt outranks the ordinary pause banners. Sending stopped
   * because the worker thinks the source changed, and that needs saying before
   * anyone reaches for the resume button.
   */
  if (config.haltedAt != null) {
    return (
      <p className="banner banner-loud" role="alert">
        <strong>Sending was halted automatically.</strong>{" "}
        {config.haltReason ?? "The worker's failsafe tripped."}{" "}
        <span className="muted">
          Stopped{" "}
          <time dateTime={config.haltedAt} title={absoluteTime(config.haltedAt)}>
            {relativeTime(config.haltedAt)}
          </time>
          . Breaches found since are recorded as seen and will not be sent.
        </span>
      </p>
    );
  }

  // An indefinite pause is the state people forget about, so it is louder.
  if (config.pausedUntil == null) {
    return (
      <p className="banner banner-loud" role="status">
        <strong>Sending is paused indefinitely.</strong> Breaches found while paused are
        recorded as seen and will never be sent — not even after resuming.
      </p>
    );
  }

  return (
    <p className="banner banner-warn" role="status">
      Sending is paused until{" "}
      <time dateTime={config.pausedUntil} title={absoluteTime(config.pausedUntil)}>
        {absoluteTime(config.pausedUntil)}
      </time>{" "}
      ({relativeTime(config.pausedUntil)}). It resumes automatically. Breaches found before then
      are recorded as seen and will not be sent.
    </p>
  );
}
